import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ExternalLink, Check, Info, HelpCircle, Download, FileImage, Printer } from 'lucide-react';
import { API } from '@/utils/auth';
import PageMeta from '@/components/PageMeta';
import REGIONS_DATA from '@/data/regions.json';

const HowToAccept = () => {
  const [services, setServices] = useState([]);
  const [features, setFeatures] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [mediaKit, setMediaKit] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [posRes, exchangesRes, mediaKitRes] = await Promise.all([
        axios.get(`${API}/pos-services`),
        axios.get(`${API}/exchanges`),
        axios.get(`${API}/media-kit`)
      ]);
      setServices(posRes.data.services || []);
      setFeatures(posRes.data.features || []);
      setExchanges(exchangesRes.data || []);
      setMediaKit(mediaKitRes.data.items || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tag color mapping
  const tagColors = [
    'bg-yellow-100 text-yellow-700',
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-purple-100 text-purple-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
    'bg-gray-100 text-gray-700'
  ];

  // Helper to render feature value
  const renderFeatureValue = (service, feature) => {
    const value = service.features?.[feature.id];
    
    if (feature.type === 'checkmark') {
      return value ? (
        <Check className="w-5 h-5 text-green-600 mx-auto" />
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    }
    
    if (feature.type === 'multi-select') {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <div className="flex flex-col gap-1 items-center">
          {value.map((v, i) => (
            <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded whitespace-nowrap">{v}</span>
          ))}
        </div>
      );
    }
    
    return value || <span className="text-muted-foreground">-</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="How to accept Bitcoin"
        path="/how-to-accept"
        description="Compare Bitcoin point-of-sale solutions, payment service providers, and exchanges to start accepting Bitcoin at your business."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {/* Header */}
        <div className="mb-8 sm:mb-12 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold font-heading text-foreground mb-3 sm:mb-4" data-testid="accept-heading">
            How Do I Accept Bitcoin?
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
            Compare point-of-sale solutions to find the right fit for your business. Each platform offers different features for accepting Bitcoin payments.
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading POS services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-xl">
            <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No POS services configured yet</h3>
            <p className="text-muted-foreground">Admin can add services via POS Management in the admin dashboard.</p>
          </div>
        ) : (
          <>
            {/* Desktop: Comparison Table */}
            <div className="hidden lg:block bg-card border border-border rounded-xl overflow-hidden mb-12">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-foreground sticky left-0 bg-muted">
                        Feature
                      </th>
                      {services.map(service => (
                        <th key={service.id} className="px-6 py-4 text-center min-w-[150px]">
                          <div className="flex flex-col items-center gap-2">
                            {service.logo_path && (
                              <img
                                src={service.logo_path.startsWith('/logos/') ? service.logo_path : service.logo_path.replace('/uploads/', '/api/uploads/')}
                                alt={service.name}
                                className="w-10 h-10 object-contain rounded"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span className="font-semibold text-foreground">{service.name}</span>
                            {service.website && (
                              <a 
                                href={service.website.startsWith('http') ? service.website : `https://${service.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                              >
                                Visit <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {features.map(feature => (
                      <tr key={feature.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4 text-sm sticky left-0 bg-card">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{feature.name}</span>
                            {feature.tooltip && (
                              <span title={feature.tooltip} className="cursor-help">
                                <Info className="w-4 h-4 text-muted-foreground" />
                              </span>
                            )}
                          </div>
                        </td>
                        {services.map(service => (
                          <td key={service.id} className="px-6 py-4 text-sm text-center">
                            {renderFeatureValue(service, feature)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: Icon Cards */}
            <div className="lg:hidden mb-12">
              <h2 className="text-lg font-heading font-semibold text-foreground mb-4">POS Solutions</h2>
              <div className="grid grid-cols-2 gap-3">
                {services.map(service => {
                  // Get key feature values
                  const posType = service.features?.pos_type;
                  const serviceType = service.features?.service_type;
                  const useType = service.features?.use_type;
                  const hasFiatOffRamp = service.features?.fiat_offramp === true;
                  const hasLightning = service.features?.lightning === true;
                  
                  return (
                    <div key={service.id} className="bg-card border border-border rounded-xl p-3 flex flex-col">
                      {/* Logo & Name */}
                      <div className="flex items-center gap-2 mb-3">
                        {service.logo_path ? (
                          <img 
                            src={`${service.logo_path.replace('/uploads/', '/api/uploads/')}`} 
                            alt={service.name}
                            className="w-10 h-10 object-contain rounded-lg flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="w-10 h-10 bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-900/30 dark:to-orange-800/20 rounded-lg flex items-center justify-center flex-shrink-0" style={{display: service.logo_path ? 'none' : 'flex'}}>
                          <span className="text-orange-600 dark:text-orange-400 font-bold">{(service.name || '?').charAt(0)}</span>
                        </div>
                        <h3 className="font-semibold text-sm text-foreground line-clamp-2">{service.name}</h3>
                      </div>
                      
                      {/* Key Info Tags */}
                      <div className="flex-1 space-y-2 mb-3">
                        {posType && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{posType}</span>
                            {serviceType && (
                              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{serviceType}</span>
                            )}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {hasLightning && (
                            <span className="text-[10px] bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-1.5 py-0.5 rounded">Lightning</span>
                          )}
                          {hasFiatOffRamp && (
                            <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded">Fiat Off-Ramp</span>
                          )}
                          {useType && (
                            <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded">{useType}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Visit Button */}
                      {service.website && (
                        <a 
                          href={service.website.startsWith('http') ? service.website : `https://${service.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
                        >
                          Visit <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
        
        {/* Additional Info */}
        <div className="mt-8 sm:mt-12 bg-accent border border-primary/20 rounded-xl p-5 sm:p-8">
          <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-3 sm:mb-4">Need Help Choosing?</h3>
          <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-muted-foreground">
            <p>
              <strong>Over the Counter:</strong> Best for physical retail locations, cafes, and restaurants where customers pay in person.
            </p>
            <p>
              <strong>Online:</strong> Ideal for e-commerce, remote payments, and mobile trades that invoice on-site (plumbers, electricians, handymen).
            </p>
            <p>
              <strong>Self-Hosted vs Hosted:</strong> Self-hosted solutions give you complete control but require technical setup. Hosted services are easier to start but involve trusting a third party.
            </p>
            <p className="pt-3 sm:pt-4">
              All solutions support Lightning Network for instant, low-cost Bitcoin transactions.
            </p>
          </div>
        </div>
        
        {/* Service Providers Section */}
        <div className="mt-12 sm:mt-16">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-2">Bitcoin Payment Service Providers</h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
            Need help setting up Bitcoin payments for your business? These service providers can implement the payment infrastructure for you.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Mineracks */}
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:shadow-md hover:border-primary/30 transition-all">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3">
                  <img 
                    src="/logos/mineracks.webp" 
                    alt="Mineracks" 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg items-center justify-center hidden">
                    <span className="text-primary font-bold text-lg sm:text-xl">M</span>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground">Mineracks</h3>
                    <p className="text-xs text-muted-foreground">Bitcoin Infrastructure Services</p>
                  </div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                Full-service Bitcoin payment infrastructure implementation. From consultation to deployment, 
                Mineracks helps businesses integrate Bitcoin and Lightning payments seamlessly.
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4">
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-orange-100 text-orange-700">Payment Setup</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Lightning Network</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-green-100 text-green-700">Consultation</span>
              </div>
              <a 
                href="https://mineracks.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                data-testid="mineracks-link"
              >
                Visit Mineracks <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* Bitcoin Merchants */}
            <div className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:shadow-md hover:border-primary/30 transition-all">
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex items-center gap-3">
                  <img 
                    src="/logos/bitcoin-merchants.png" 
                    alt="Bitcoin Merchants" 
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-contain bg-black"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg items-center justify-center hidden">
                    <span className="text-orange-600 font-bold text-lg sm:text-xl">₿</span>
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-foreground">Bitcoin Merchants</h3>
                    <p className="text-xs text-muted-foreground">Australian Bitcoin Payment Solutions</p>
                  </div>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                Helping Australian businesses accept Bitcoin payments. Bitcoin Merchants provides 
                tailored solutions and support for merchants looking to integrate Bitcoin into their operations.
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4">
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-orange-100 text-orange-700">Merchant Support</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Payment Solutions</span>
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">Australian Focus</span>
              </div>
              <a 
                href="https://www.bitcoinmerchants.com.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                data-testid="bitcoin-merchants-link"
              >
                Visit Bitcoin Merchants <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            
            {/* Placeholder for more providers */}
            <div className="bg-muted/50 border border-dashed border-border rounded-xl p-5 sm:p-6 flex flex-col items-center justify-center text-center min-h-[200px]">
              <HelpCircle className="w-8 h-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">Are you a Bitcoin payment service provider?</p>
              <a 
                href="/contact"
                className="text-sm text-primary hover:underline"
              >
                Contact us to be listed
              </a>
            </div>
          </div>
        </div>
        
        {/* Bitcoin Exchanges by Region */}
        <div className="mt-12 sm:mt-16" id="exchanges">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-2">Bitcoin Exchanges</h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">Buy Bitcoin directly from these Bitcoin-focused exchanges. All support Lightning Network and prioritise self-custody.</p>

          {/* Region anchor-link nav — only regions that have exchanges */}
          {(() => {
            const REGIONS = [
              ...['oceania', 'asia', 'americas', 'europe', 'africa']
                .map(slug => ({ slug, label: REGIONS_DATA[slug].name })),
              { slug: 'global', label: 'Global / P2P' },
            ];

            const exchangesByRegion = (slug) => {
              if (slug === 'global') {
                return exchanges
                  .filter(ex => ex.is_p2p_global)
                  .sort((a, b) => (a.name || '?').localeCompare(b.name || '?'));
              }
              return exchanges
                .filter(ex => !ex.is_p2p_global)
                .filter(ex => (ex.region && ex.region.toLowerCase() === slug)
                  || (ex.regions || []).some(r => typeof r === 'string' && r.toLowerCase() === slug))
                .sort((a, b) => (a.name || '?').localeCompare(b.name || '?'));
            };

            const populatedRegions = REGIONS.filter(r => exchangesByRegion(r.slug).length > 0);

            const ExchangeCard = ({ exchange }) => {
              const logoSrc = exchange.logo_path
                ? exchange.logo_path.startsWith('/logos/')
                  ? exchange.logo_path
                  : exchange.logo_path.replace('/uploads/', '/api/uploads/')
                : null;
              return (
              <div className="bg-card border border-border rounded-xl p-5 sm:p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  <div className="flex items-center gap-2 sm:gap-3">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={exchange.name}
                        className="w-8 sm:w-10 h-8 sm:h-10 object-contain flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="w-8 sm:w-10 h-8 sm:h-10 bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-900/30 dark:to-orange-800/20 rounded-lg items-center justify-center flex-shrink-0"
                      style={{ display: logoSrc ? 'none' : 'flex' }}
                    >
                      <span className="text-orange-600 dark:text-orange-400 font-bold text-sm">{(exchange.name || '?').charAt(0)}</span>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-semibold text-foreground">
                        {exchange.name}
                        {exchange.is_p2p_global && (
                          <span className="ml-2 text-[10px] sm:text-xs font-normal bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full align-middle">Global P2P</span>
                        )}
                      </h3>
                    </div>
                  </div>
                  {exchange.url && (
                    <a
                      href={exchange.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1 text-xs sm:text-sm"
                    >
                      Visit <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {exchange.description && (
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                    {exchange.description}
                  </p>
                )}
                {exchange.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {exchange.tags.map((tag, i) => (
                      <span key={i} className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded ${tagColors[i % tagColors.length]}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
            }

            return (
              <>
                {/* Jump-to nav */}
                {populatedRegions.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground mb-8">
                    <span className="font-medium text-foreground">Jump to:</span>
                    {populatedRegions.map((r, i) => (
                      <span key={r.slug} className="flex items-center gap-1">
                        <a
                          href={`#exchanges-${r.slug}`}
                          className="text-primary hover:underline"
                        >
                          {r.label}
                        </a>
                        {i < populatedRegions.length - 1 && <span className="text-border">·</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* Region sections */}
                {REGIONS.map(region => {
                  const regionExchanges = exchangesByRegion(region.slug);
                  return (
                    <div key={region.slug} id={`exchanges-${region.slug}`} className="mb-10 sm:mb-12">
                      <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-1">{region.label}</h3>
                      <div className="w-12 h-0.5 bg-primary/40 mb-4 sm:mb-6 rounded-full" />
                      {regionExchanges.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                          {regionExchanges.map(exchange => (
                            <ExchangeCard key={exchange.id} exchange={exchange} />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic py-4">
                          No exchanges listed for this region yet.
                        </p>
                      )}
                    </div>
                  );
                })}
              </>
            );
          })()}
        </div>
        
        {/* Merchant Media Kit */}
        <div className="mt-12 sm:mt-16" id="media-kit">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl font-heading font-bold text-foreground mb-2">Merchant Media Kit</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
              Download free marketing materials to let your customers know you accept Bitcoin. 
              Print-ready designs featuring the Orange Label branding.
            </p>
            <p className="text-xs sm:text-sm text-primary mt-2 font-medium">
              "Discover More Places to Spend Bitcoin"
            </p>
          </div>
          
          {mediaKit.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {mediaKit.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all group"
                  data-testid={`media-kit-${item.id}`}
                >
                  {/* Preview Area - no border/background */}
                  <div className="p-2 flex items-center justify-center min-h-[160px] sm:min-h-[200px]">
                    {item.preview_url ? (
                      <img 
                        src={item.preview_url}
                        alt={item.name}
                        className="max-w-full max-h-[160px] sm:max-h-[190px] object-contain group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="text-center">
                        <FileImage className="w-16 h-16 text-muted-foreground mx-auto mb-2" />
                        <span className="text-xs text-muted-foreground uppercase font-medium">{item.format} File</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Info & Download */}
                  <div className="p-4 sm:p-5 border-t border-border">
                    <div className="mb-2">
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">{item.name}</h3>
                      <p className="text-xs text-muted-foreground">{item.size}</p>
                    </div>
                    
                    <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                      {item.description}
                    </p>
                    
                    <a
                      href={item.download_url}
                      download={`orangelabel-${item.id}.${item.format}`}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download {item.format.toUpperCase()}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-muted rounded-xl">
              <FileImage className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-sm sm:text-base">Media kit assets loading...</p>
            </div>
          )}
          
          {/* Tips Section */}
          <div className="mt-6 sm:mt-8 bg-accent border border-primary/20 rounded-xl p-5 sm:p-6">
            <h3 className="text-base sm:text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-primary" />
              Printing Tips
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-xs sm:text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground mb-1">For Stickers & Decals</p>
                <p>Use weather-resistant vinyl for outdoor applications. Most local print shops can produce from SVG files.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">For Signage</p>
                <p>Print on thick cardstock (300gsm+) or have it laminated for durability. A6 fits standard photo frames.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Converting to PDF</p>
                <p>Open SVG in browser → Print → Save as PDF. Or use free tools like Inkscape for more control.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">Custom Branding</p>
                <p>SVG files are editable - add your business name or QR code using any vector editor.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToAccept;
